import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const TOKEN_KEY = 'ifaap_session_token';

// Auth tokens live in sessionStorage (cleared when the tab closes) for better
// XSS hardening. We still read legacy values from localStorage during a
// migration window so users already logged-in (and Playwright tests that seed
// the token via localStorage) keep working transparently.
const _safeGet = (storage) => {
  try { return storage.getItem(TOKEN_KEY) || null; } catch { return null; }
};
const _safeRemove = (storage) => {
  try { storage.removeItem(TOKEN_KEY); } catch { /* noop */ }
};

export const getToken = () => {
  // sessionStorage wins; legacy localStorage tokens are migrated on read.
  let t = _safeGet(sessionStorage);
  if (t) return t;
  t = _safeGet(localStorage);
  if (t) {
    try { sessionStorage.setItem(TOKEN_KEY, t); } catch { /* noop */ }
    _safeRemove(localStorage);
  }
  return t;
};

export const setToken = (t) => {
  try {
    if (t) {
      sessionStorage.setItem(TOKEN_KEY, t);
      _safeRemove(localStorage); // ensure no stale long-term copy
    } else {
      _safeRemove(sessionStorage);
      _safeRemove(localStorage);
    }
  } catch { /* noop */ }
};

const api = axios.create({
  baseURL: API_BASE,
  // Keep cookie-based auth as best-effort (it works when CORS allows it), but
  // the Authorization header below is the authoritative mechanism because the
  // Kubernetes ingress rewrites ACAO to "*" and drops credentials cross-origin.
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${t}`;
  }
  // Vague 97.13 — Acting Org for super_admin cross-OF browsing
  try {
    const acting = sessionStorage.getItem('ifaap_acting_org');
    if (acting) {
      const parsed = JSON.parse(acting);
      if (parsed?.org_id) config.headers['X-Acting-Org-Id'] = parsed.org_id;
    }
  } catch { /* noop */ }
  // Vague 113 L13 — Mailbox account selector (FE override of dispatcher)
  try {
    const mailboxAcct = sessionStorage.getItem('ifaap_mailbox_account');
    if (mailboxAcct && config.url && config.url.includes('/mailbox')) {
      config.headers['X-Mailbox-Account'] = mailboxAcct;
    }
  } catch { /* noop */ }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    // Vague 113 Wave 1u3 — HTTP 424 Failed Dependency is reserved for
    // *upstream* provider auth failures (Microsoft Graph / Gmail / IMAP token
    // expired). It must NOT drop the OFManager session token — the user is
    // still logged-in to OFManager itself; only their mailbox needs reconnect.
    if (err?.response?.status === 424) {
      try {
        window.dispatchEvent(new CustomEvent('ifaap:mailbox-auth-expired', {
          detail: { message: err.response.data?.detail || 'Reconnectez votre boîte email.' },
        }));
      } catch (e) { /* noop */ }
    } else if (err?.response?.status === 401 && getToken()) {
      // Real session 401 — token invalid/expired. Drop it so user can re-login cleanly.
      setToken(null);
    }
    if (err?.response?.status === 402) {
      // Quota exceeded — broadcast for the UpsellModal to render globally.
      try {
        const detail = err.response.data?.detail || {};
        window.dispatchEvent(new CustomEvent('ifaap:quota-exceeded', { detail }));
      } catch (e) { /* noop */ }
    }
    return Promise.reject(err);
  },
);

export default api;
